<script setup lang="ts">
import type { PressPost } from '@nimbus/types'

definePageMeta({
    hidePromotions: true
})

const filters = [
    {
        label: 'Press releases',
        value: 'press releases'
    },
    {
        label: 'Oldest',
        value: 'oldest'
    },
    {
        label: 'Newest',
        value: 'newest'
    }
]

// The native app's WebView can't download the zip files below: the load fails and the app shows its error screen.
// isMobileApp reads window, so it is false on the server; gating on isMounted keeps hydration consistent.
const { isMobileApp } = useMobileApp()
const isMounted = useMounted()
const showDownloads = computed(() => !(isMounted.value && isMobileApp))

// Press articles: read from Nuxt Content.
const { data: pressArticles } = await usePressData()

// Bridge the content shape to PressPost[] (fields overlap); null -> undefined for the optional prop.
const articles = computed(() => (pressArticles.value ?? undefined) as PressPost[] | undefined)

// SEO — there is no Content `press` *page* entity, so use the static fallback.
const { getHead, getSeoMeta } = useContentSeo({
    metaTitle: 'Press | Happytiger.co.uk',
    metaDescription: 'Here you can find the most recent articles and press releases from Happytiger.co.uk'
})

useHead(getHead())
useSeoMeta(getSeoMeta())
// ./ SEO
</script>

<template>
    <div class="press-page">
        <NimbusPressApp
            title="Press"
            subtitle="Here you can find the most recent articles and press releases from Happytiger.co.uk"
            search-field-placeholder="Search"
            :error-options="{
                noSearchResultText: 'Your search did not return any results.',
                noPostsText: 'We have no more posts to show on this page, please try again later.'
            }"
            :filters="filters"
            :articles="articles"
        />
        <div v-if="showDownloads" class="press-page__sidebar">
            <div class="press-page__sidebar--panel">
                <span class="press-page__sidebar--label">4 images</span>
                <NuxtImg
                    src="/media/logo/logo-on-bg.jpg"
                    preset="local"
                    provider="cloudflare"
                    decoding="async"
                    loading="lazy"
                    alt="Happytiger.co.uk logo"
                    densities="1x 2x"
                />
                <UIButton
                    class="press-page__sidebar--button"
                    size="sm"
                    external
                    href="/media/press/logo-pack.zip"
                    type="primary"

                    :block="true"
                >
                    <UIIcon
                        name="download"
                        color="#fff"
                        size="14"
                    />
                    Download logo package
                </UIButton>
            </div>
            <div class="press-page__sidebar--panel">
                <span class="press-page__sidebar--label">35 images</span>
                <NuxtImg
                    src="/media/press/press_thumb.jpg"
                    preset="local"
                    provider="cloudflare"
                    decoding="async"
                    loading="lazy"
                    alt="Office image from Happytiger.co.uk"
                />
                <UIButton
                    class="press-page__sidebar--button"
                    size="sm"
                    external
                    href="/media/press/press_photos.zip"
                    type="primary"

                    :block="true"
                >
                    <UIIcon
                        name="download"
                        color="#fff"
                        size="14"
                    />
                    Download press photos
                </UIButton>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.press-page {
    display: flex;
    flex-direction: column;
    width: 100%;

    @include tablet(md) {
        flex-direction: row;
        gap: 30px;
    }

    &__sidebar {
        margin-top: 20px;

        @include tablet(md) {
            width: 33.333%;
            margin-top: 85px; // Add missing space to align with press links
        }

        @include desktop {
            width: 25%;
        }

        &--label {
            background-color: $gray-light;
            color: $white;
            border-radius: 6px;
            font-size: $font-size-xxs;
            font-weight: $text-bold;
            padding: 2px 5px;
            position: absolute;
            right: 0;
            top: 0;
            z-index: 1;
        }

        &--panel {
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 15px;
            border-radius: 6px;
            background-color: var(--panel-bg, $panel-color);
            box-shadow: $element-box-shadow;
            gap: 15px;
            margin-bottom: 20px;

            img {
                width: 100%;
                height: 100px;
                border-radius: 6px;
                object-fit: cover;
                overflow: clip;
                overflow-clip-margin: content-box;

                @include tablet(md) {
                    height: 70px;
                }
            }
        }

        &--heading {
            font-weight: $text-bold;
            font-size: $font-size-lg;
            margin: 20px 0 10px 0;

            @include tablet(md) {
                font-size: 30px;
                font-family: $brand-font-family;
                margin: 10px 0;
            }
        }

        &--button {
            @include tablet(md) {
                font-size: $font-size-xs;
            }
        }
    }
}
</style>
